// ignore-string-externalization

import React, { MutableRefObject, Ref } from 'react';
import styled from 'styled-components';
import {
  body2FontSize,
  cta1LetterSpacing,
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  screenXsMax,
  screenLgMin,
  heading3FontSize,
  heading3LineHeight,
  spacer40,
  heading4FontSize,
  Type,
  spacer64,
} from '@spotify-internal/encore-web-v5';
import { IconArrowRight } from '@spotify-internal/encore-web-v6';
import {
  DELAYS,
  fadeInAnimation,
  withAnimationContext,
  Grid,
  TWO_COLUMN_MAX_WIDTH,
  AnimationContextType,
} from '@spotify-internal/frodor-modules';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { ModuleType } from '../types';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  ImageAspectBox1x1,
  ButtonTertiaryAsLink,
  Supertext,
  Link,
  CUSTOM_IPAD_WIDTH,
} from '../../ui';
import { formatText, fixHref, withoutProps } from '../../utils';
import { Colors } from '../../ui/colorsType';

const StyledSupertext = styled(Supertext)`
  padding-bottom: ${spacer16};
  font-size: ${body2FontSize};
  letter-spacing: ${cta1LetterSpacing};
`;

const Header = styled(Grid)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  /* height: auto; */
  padding-top: 104px;
  padding-bottom: ${spacer24};

  &.tallest {
    height: 100%;
  }

  @media (min-width: ${screenLgMin}) {
    padding-bottom: ${spacer40};
  }

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding-top: ${spacer64};
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  grid-column: 8 / 13;
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
    order: 1;
  }
`;

const InnerText = styled.div`
  grid-column: 1 / 7;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 1080px) {
    grid-column: 1 / 7;
  }
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding-top: ${spacer40};
    grid-column: 1 / -1;
    order: 2;
    padding-bottom: ${spacer32};
  }
`;

// TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific
const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
  })<{ textColor: string; animationContext: AnimationContextType }>`
    ${fadeInAnimation(DELAYS.SECOND)};
    font-size: clamp(32px, 5vw, 94px);
    line-height: clamp(54px, 5vw, 104px);
    padding-bottom: ${spacer32};
    max-width: 684px;

    @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
      padding-top: ${spacer8};
      padding-bottom: ${spacer24};
      font-size: 48px;
      line-height: 54px;
    }

    @media (max-width: ${CUSTOM_IPAD_WIDTH}) {
      text-align: left;
    }
  `,
);

const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'cello',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-bottom: ${spacer32};
    font-size: ${heading3FontSize};
    line-height: ${heading3LineHeight};

    @media (max-width: 1600px) {
      font-size: ${heading4FontSize};
      line-height: ${heading3FontSize};
    }
  `,
);

const StyledLink = styled(withoutProps(Link, 'textColor'))<{
  textColor: string;
}>`
  color: ${({ textColor }) => textColor};

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    color: ${(props: Colors) => hexToRgba(props.textColor || '', 0.75)};
  }
`;

const FeaturedContentLink = withAnimationContext(
  styled(withoutProps(ButtonTertiaryAsLink, 'textColor'))<{
    textColor: string;
    animationContext: AnimationContextType;
  }>`
    ${fadeInAnimation(DELAYS.FOURTH)};
    color: ${({ textColor }) => textColor};
    align-self: flex-start;
    &&:hover:not(:disabled),
    &&:focus:not(:disabled) {
      color: ${({ textColor }) => hexToRgba(textColor || '', 0.75)};
    }

    margin-bottom: ${spacer32};
    @media (max-width: ${screenXsMax}) {
      margin-bottom: 0px;
    }

    svg {
      [dir='rtl'] & {
        transform: scaleX(-1);
      }
    }
  `,
);

const getHeadline = (
  ctaText: string,
  ctaLink: string,
  headline: string,
  textColor: string,
  trackCat: string,
) => {
  if (headline) {
    const HeadlineElement = (
      <Headline
        textColor={textColor}
        data-testid="header-module-headline"
        dangerouslySetInnerHTML={{ __html: formatText(headline) }}
      />
    );
    if (ctaText && ctaLink) {
      return (
        <StyledLink
          textColor={textColor}
          href={fixHref(ctaLink)}
          track={[trackCat, `CTA Link - ${ctaText}`]}
        >
          {HeadlineElement}
        </StyledLink>
      );
    }
    return HeadlineElement;
  }
  return <></>;
};

export type HeaderModuleType = ModuleType & {
  headline: string;
  subtext?: string;
  supertext?: string;
  image: ContentfulAsset;
  ctaLink?: string;
  ctaText?: string;
  backgroundColor: string;
  textColor: string;
};

const TRACK_CAT = 'module-heroHeader';

export const HeaderModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeaderModuleType
>(
  (
    {
      headline = '',
      subtext = '',
      supertext = '',
      image,
      ctaLink = '',
      ctaText = '',
      backgroundColor = '',
      textColor = '',
    },
    ref,
  ) => {
    return (
      <Header
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as Ref<MutableRefObject<ModuleType>>}
      >
        <InnerText>
          {supertext && <StyledSupertext>{supertext}</StyledSupertext>}
          {getHeadline(ctaText, ctaLink, headline, textColor, TRACK_CAT)}
          {subtext && (
            <Subtext
              dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
            />
          )}
          {ctaText && ctaLink && (
            <FeaturedContentLink
              textColor={textColor}
              href={fixHref(ctaLink)}
              track={[TRACK_CAT, `CTA Link - ${ctaText}`]}
              iconTrailing={() => <IconArrowRight iconSize={16} />}
              condensedAll
            >
              {ctaText}
            </FeaturedContentLink>
          )}
        </InnerText>
        <ImageContainer>
          {image && <ContentfulImage asset={image} />}
        </ImageContainer>
      </Header>
    );
  },
);
